//src/components/Dialogs/PublishingDialog.js

import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Typography,
  Box,
  IconButton,
  Tooltip,
  TextField,
  Button,
} from '@mui/material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Share from '@mui/icons-material/Share';

/**
 * PublishingDialog
 *
 * Props:
 * - open (bool): Whether the dialog is open
 * - publishedFormId (string | null): Form ID if publishing succeeded
 * - vanitySlug (string | null): Vanity slug if set for the form (optional)
 * - onClose (function): Called when the user clicks "Done" or clicks outside the dialog
 */
const PublishingDialog = (
  { open, publishedFormId, vanitySlug, onClose, onShare },
) => {
  const isPublished = Boolean(publishedFormId);

  // Build URLs:
  // - canonical: always valid internal route
  // - vanity: preferred for sharing if present
  const canonicalUrl = useMemo(() => {
    return publishedFormId ?
      `${window.location.origin}/forms/${publishedFormId}` :
      '';
  }, [publishedFormId]);

  const vanityUrl = useMemo(() => {
    const slug = String(vanitySlug || '').trim().toLowerCase();
    if (!publishedFormId || !slug) return '';
    return `https://${slug}.utmsmartmanager.com`;
  }, [publishedFormId, vanitySlug]);

  // Prefer vanity for the primary share/copy target
  const fullPublishedUrl = vanityUrl || canonicalUrl;

  const handleCopy = () => {
    if (fullPublishedUrl) {
      navigator.clipboard.writeText(fullPublishedUrl);
      console.log(
        '[PublishingDialog] Copied published form URL to clipboard:',
        fullPublishedUrl,
      );
    }
  };

  const handleShare = () => {
    if (onShare) onShare();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { p: 3, borderRadius: 2 },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        {isPublished ? '🎉 Hooray! Your form is now live.' : 'Publishing Your Form'}
      </DialogTitle>

      <DialogContent>
        {isPublished ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Share your form with the world or copy the link below.
            </Typography>

            <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
              <TextField
                fullWidth
                size="small"
                value={fullPublishedUrl}
                InputProps={{ readOnly: true }}
                label="Form URL"
              />

              <Tooltip title="Copy link">
                <IconButton onClick={handleCopy}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Share">
                <IconButton onClick={handleShare}>
                  <Share fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              We’re saving your form, generating a preview, and uploading assets. Hang tight!
            </Typography>
            <LinearProgress sx={{ width: '100%' }} />
          </Box>
        )}
      </DialogContent>

      {isPublished && (
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PublishingDialog;
